import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchOAuthConnections, unlinkOAuthConnection} from '@/entities/account/api';
import {ApiError} from '@/shared/api';
import {formatTimestamp} from '@/shared/lib/format';
import {Banner} from '@/shared/ui/banner';
import {EmptyState} from '@/shared/ui/empty-state';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {Skeleton} from '@/shared/ui/skeleton';
import {radii, spacing} from '@/shared/ui/theme';

/**
 * Social identities linked to this account, and the one write: unlinking one.
 *
 * ## The lockout this refuses to cause
 *
 * An account signed up through Google has no password. Unlink its only connection and
 * there is no way left to sign in at all — no password to type, no identity to present
 * — and nothing in the API stops it: `DELETE /auth/oauth/connections/{id}` does what it
 * is told. So the last connection is not offered for unlinking while
 * `hasPassword` is false, and the row says why rather than being silently inert.
 *
 * `hasPassword` comes from the caller (`profile.has_password`), which the security
 * screen has already fetched — asking for it a second time here would mean two screens
 * disagreeing about the same fact for as long as one cache was stale.
 */
const styles = StyleSheet.create({
  section: {gap: spacing(2)},
});

export function OAuthConnectionsCard({hasPassword}: {hasPassword: boolean}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const connections = useQuery({
    queryKey: ['oauth-connections'],
    queryFn: fetchOAuthConnections,
  });

  const unlink = useMutation({
    mutationFn: (id: string) => unlinkOAuthConnection(id),
    onSuccess: async () => {
      setConfirmId(null);
      await qc.invalidateQueries({queryKey: ['oauth-connections']});
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  const rows = connections.data?.connections ?? [];
  /** The last way in, with no password behind it. Unlinking this locks the account. */
  const isOnlyWayIn = rows.length === 1 && !hasPassword;

  return (
    <View style={styles.section}>
      {connections.isPending ? (
        <Skeleton height={90} radius={radii.lg} />
      ) : connections.isError ? (
        <Banner tone="error" title={errorOf(connections.error)} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('oauth.empty')} />
      ) : (
        <RowGroup>
          {rows.map(connection => (
            <ListRow
              key={connection.id}
              icon="link"
              // The provider is a machine token (`google`), so it is translated with a
              // fall-back to itself: a provider added after this build still reads.
              title={t(`oauth.provider.${connection.provider}`, connection.provider)}
              subtitle={
                connection.email ||
                (connection.created_at
                  ? t('oauth.linkedAt', {at: formatTimestamp(connection.created_at)})
                  : undefined)
              }
              value={
                isOnlyWayIn
                  ? t('oauth.onlyWayIn')
                  : confirmId === connection.id
                  ? t('oauth.confirmUnlink')
                  : t('oauth.unlink')
              }
              onPress={
                isOnlyWayIn
                  ? undefined
                  : () =>
                      confirmId === connection.id
                        ? unlink.mutate(connection.id)
                        : setConfirmId(connection.id)
              }
              testID={`oauth-${connection.id}`}
            />
          ))}
        </RowGroup>
      )}

      {isOnlyWayIn ? (
        <Banner tone="info" title={t('oauth.onlyWayInHint')} />
      ) : null}
      {unlink.isError ? <Banner tone="error" title={errorOf(unlink.error)} /> : null}
    </View>
  );
}
